import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Link} from 'react-router-dom';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import ConfirmModal from 'components/ConfirmModal';
import ProductTimerButtons from 'components/ProductTimerButtons';
import {fetchMoreProducts, refreshProducts} from 'ducks/products';
import {fetchWorkLogs} from 'ducks/workLogs';
import api from 'utils/api';
import {USER_LANG} from 'utils/locale';
import {workLogsType} from 'propTypes/products';
import {gettext} from 'utils/text';
import {WORKERS} from 'constants/accounts';
import {ORDERS} from 'constants/sales';


class ProductList extends React.Component {
    static propTypes = {
        products: PropTypes.arrayOf(PropTypes.shape({})),
        userId: PropTypes.number.isRequired,
        next: PropTypes.string,
        error: PropTypes.string,
        isLoading: PropTypes.bool,
        workLogs: workLogsType.isRequired,
        fetchMoreProducts: PropTypes.func.isRequired,
        fetchWorkLogs: PropTypes.func.isRequired,
        refreshProducts: PropTypes.func.isRequired,
        isQualityWorker: PropTypes.bool.isRequired,
    };

    static defaultProps = {
        products: null,
        next: null,
        error: null,
        isLoading: false,
    };

    state = {
        confirmModal: null,
    };

    componentDidMount() {
        this.props.refreshProducts();
        this.props.fetchWorkLogs(null, this.props.userId);
    }

    onModalConfirm = (product) => {
        api.productionQualityControl.post(
            {product_id: product.id}, {
                estimated_vs_actual: '',
                tolerance: '',
                welding_quality: '',
                action: 'approve',
                comment: '',
                next_status: product.previous_statuses[0].value,
            },
        ).then(
            this.props.refreshProducts,
            console.error,
        );
    };

    getActiveWorkLog = (activeGroups) => {
        const activeWorkLogs = activeGroups.filter(g => g.end_time === null);
        if (!activeWorkLogs.length) {
            return null;
        }
        return activeWorkLogs[0];
    };

    hideModal = () => {
        this.setState({confirmModal: null});
    };

    handleRefresh = () => {
        this.props.refreshProducts();
        this.refreshAllWorkLogs();
    };

    refreshAllWorkLogs = () => {
        this.props.fetchWorkLogs(null, this.props.userId);
    };

    handleOkButton = (event, product) => {
        event.preventDefault();
        this.setState({
            confirmModal: {
                description: gettext('Are you sure you want approve the quality of this product?'),
                onConfirm: () => this.onModalConfirm(product),
            },
        });
    };

    filteredWorkLogGroups = productId =>
        this.props.workLogs.allWorkLogs
            .filter(workLog => workLog.product === productId);

    isProductWorkLogGroup = (workLogGroup, productId) => workLogGroup.work_logs.some(wl => wl.product === productId);

    renderLoading = () => (
        <div className="text-center text-info my-5">
            <FontAwesomeIcon icon="spinner" pulse size="6x" />
        </div>
    );

    renderEmpty = () => (
        <div className="alert alert-info" role="alert">
            {gettext('There are no products for you')}
        </div>
    );

    renderError = () => (
        <div className="alert alert-danger" role="alert">
            {this.props.error}
        </div>
    );

    renderProduct = (product) => {
        const activeGroup = this.getActiveWorkLog(this.props.workLogs.activeGroups);
        const isProductWorkLogGroup = !!activeGroup && this.isProductWorkLogGroup(activeGroup, product.id);
        const inProgress = activeGroup && isProductWorkLogGroup;
        const isPaused = this.filteredWorkLogGroups(product.id).length > 0;
        const previousOnlyWelding = product.previous_statuses.length === 1 &&
            product.previous_statuses[0].value === ORDERS.STATUS.WELDING;

        const linkToDetail = this.props.isQualityWorker
            ? `/${USER_LANG}/production/product/${product.id}/quality-control`
            : `/${USER_LANG}/production/product/${product.id}`;

        // Product is in progress by another user if it is global progress status is true, current user is different
        // from the user who started the worklog and in progress status of the product for current user is false
        const productIsActive = !inProgress && product.active_worklog.in_progress
            && product.active_worklog.user_id !== this.props.userId;

        const pointerEvent = productIsActive ? 'none' : 'auto';

        return (
            <div
                className={classNames('product-row card-deck mt-3', {overlay: productIsActive})}
                key={product.id}
                style={{pointerEvents: pointerEvent}}
            >
                <Link
                    className={classNames(
                        'card fs-h3',
                        {
                            'product-in-progress': inProgress,
                            'product-paused': !inProgress && isPaused,
                        },
                    )}
                    to={linkToDetail}
                >
                    <div
                        className="overlay-text text-center"
                        style={{display: productIsActive ? 'block' : 'none'}}
                    >
                        {gettext('Another user currently is working on this product')}
                    </div>
                    <div className="card-body row mr-3">
                        <div className="col-3 d-flex flex-column justify-content-center">
                            <div>{product.order.number}</div>
                        </div>
                        <div className="col-9 d-flex align-items-center">
                            <div className="mr-3">
                                <div className="text-muted">{product.article_code}</div>
                                <div className="text-muted">{product.article_name}</div>
                            </div>
                            <span className="ml-auto d-inline-flex align-items-center">
                                {this.props.isQualityWorker &&
                                <small className="text-info mr-4">
                                    {product.order.status_display}
                                </small>
                                }
                                <FontAwesomeIcon icon="chevron-right" size="2x" />
                            </span>
                        </div>
                    </div>
                </Link>
                {!this.props.isQualityWorker &&
                <ProductTimerButtons
                    productId={product.id}
                    showAddWorker={false}
                    workers={[]}
                    workLogsLoading={this.props.workLogs.isLoading}
                    workLogsError={this.props.workLogs.error}
                    workLogGroups={this.filteredWorkLogGroups(product.id)}
                    activeWorkLogGroups={this.props.workLogs.activeGroups}
                    handleRefresh={this.handleRefresh}
                    refreshAllWorkLogs={this.refreshAllWorkLogs}
                    inProgressForCurrentUser={inProgress}
                    isListLayout
                />
                }
                {this.props.isQualityWorker && product.order.quality_check_required && !previousOnlyWelding &&
                <div
                    onClick={e => this.handleOkButton(e, product)}
                    className="btn btn-success d-flex px-5 justify-content-center align-items-center flex-no-grow-fit"
                >
                    <FontAwesomeIcon icon="thumbs-up" size="2x" />
                </div>
                }
            </div>
        );
    };

    renderProductList = () => {
        if (this.props.isQualityWorker) {
            const qualityControlRequiredProducts = this.props.products.filter(p => p.order.quality_check_required);
            const otherProducts = this.props.products.filter(p => !p.order.quality_check_required);

            return (
                <div>
                    <ConfirmModal modal={this.state.confirmModal} onHide={this.hideModal} />
                    <h4 className="mt-4">{gettext('Quality control required')}</h4>
                    {qualityControlRequiredProducts.length
                        ? qualityControlRequiredProducts.map(this.renderProduct)
                        : this.renderEmpty()
                    }
                    <h4 className="mt-4">{gettext('Other products')}</h4>
                    {otherProducts.length
                        ? otherProducts.map(this.renderProduct)
                        : this.renderEmpty()
                    }
                    {this.props.next ? this.renderLoadMore() : null}
                </div>
            );
        }

        return (
            <div>
                {this.props.products.map(this.renderProduct)}
                {this.props.next ? this.renderLoadMore() : null}
            </div>
        );
    };

    renderLoadMore = () => (
        <button
            className="btn btn-info btn-block mt-4"
            onClick={() => this.props.fetchMoreProducts()}
            disabled={this.props.isLoading}
        >
            {gettext('Load more')}
        </button>
    );

    render() {
        let content = null;
        if (this.props.error) {
            // Loading has failed
            content = this.renderError();
        } else if (!this.props.products || this.props.isLoading) {
            // Loading in progress
            content = this.renderLoading();
        } else if (this.props.products.length) {
            // Have some results
            content = this.renderProductList();
        } else {
            // Empty list has been loaded
            content = this.renderEmpty();
        }

        return (
            <div className="mx-auto w-lg mb-5">
                <div className="clearfix mb-3">
                    <h3 className="float-left mb-0">{gettext('Products')}</h3>
                    <button
                        className="float-right btn btn-info"
                        onClick={this.handleRefresh}
                        disabled={this.props.isLoading}
                    >
                        {gettext('Refresh')}
                    </button>
                </div>
                {content}
            </div>
        );
    }
}

const mapStateToProps = state => ({
    products: state.products.products,
    next: state.products.next,
    error: state.products.error,
    isLoading: state.products.isLoading,
    isQualityWorker: state.workers.worker && state.workers.worker.role === WORKERS.ROLES.QUALITY.value,
    userId: state.workers.worker.id,
    workLogs: state.workLogs,
});

const mapDispatchToProps = dispatch => ({
    fetchMoreProducts: () => dispatch(fetchMoreProducts()),
    fetchWorkLogs: (productId, userId) => dispatch(fetchWorkLogs(productId, userId)),
    refreshProducts: () => dispatch(refreshProducts()),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(ProductList);



// WEBPACK FOOTER //
// ./src/components/ProductList.js